import * as React from 'react';
import {
 Plus, Search, Edit, Trash, Save, X, AlertTriangle, Check, ArrowRight
} from 'lucide-react';
import {
 Button
} from 'shared/components/ui/button';
import {
 Card, CardContent, CardHeader, CardTitle
} from 'shared/components/ui/card';
import {
 Input
} from 'shared/components/ui/input';
import {
 Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from 'shared/components/ui/table';
import {
 Tooltip, TooltipContent, TooltipProvider, TooltipTrigger
} from 'shared/components/ui/tooltip';
import {
 Badge
} from 'shared/components/ui/badge';
import {
 Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from 'shared/components/ui/select';
import {
 useState
} from 'react';

// Define types for Rule and RuleType
interface Rule {
    id: string;
    name: string;
    condition: string;
    action: string;
    type: 'notification' | 'automation' | 'validation';
    isEditing?: boolean; // Optional flag to mark the rule as being edited
}

interface RuleType {
    value: 'notification' | 'automation' | 'validation';
    label: string;
}

const initialRules: Rule[] = [
    {
        id: '1',
        name: 'Notify on Status Change',
        condition: 'When Status changes',
        action: 'Send email notification',
        type: 'notification',
    },
    {
        id: '2',
        name: 'Set Due Date',
        condition: "When Status is 'In Progress'",
        action: 'Set Due Date to 7 days from now',
        type: 'automation',
    },
    {
        id: '3',
        name: 'Validate Budget',
        condition: 'When Budget is updated',
        action: 'Ensure Budget is greater than $0',
        type: 'validation',
    },
];

const ruleTypes: RuleType[] = [
    {
 value: 'notification',
label: 'Notification'
},
    {
 value: 'automation',
label: 'Automation'
},
    {
 value: 'validation',
label: 'Validation'
},
];

const getRuleTypeColor = (type: Rule['type']) => {
    switch (type) {
        case 'notification':
            return 'bg-blue-100 text-blue-800 border-blue-200';
        case 'automation':
            return 'bg-green-100 text-green-800 border-green-200';
        case 'validation':
            return 'bg-amber-100 text-amber-800 border-amber-200';
        default:
            return 'bg-gray-100 text-gray-800 border-gray-200';
    }
};

const getRuleTypeIcon = (type: Rule['type']) => {
    switch (type) {
        case 'notification':
            return <AlertTriangle className="h-4 w-4 mr-1" />;
        case 'automation':
            return <ArrowRight className="h-4 w-4 mr-1" />;
        case 'validation':
            return <Check className="h-4 w-4 mr-1" />;
        default:
            return null;
    }
};

interface TableRulesProps {
    tableId: string;
}

// eslint-disable-next-line
export function TableRules(_: TableRulesProps) {
    const [rules, setRules] = useState<Rule[]>(initialRules);
    const [editingRule, setEditingRule] = useState<string | null>(null); // Holds the id of the editing rule
    const [searchTerm, setSearchTerm] = useState<string>('');

    const filteredRules = rules.filter(
        (rule) =>
            rule.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            rule.condition.toLowerCase().includes(searchTerm.toLowerCase()) ||
            rule.action.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleAddRule = () => {
        const newRule: Rule = {
            id: `${Date.now()}`,
            name: 'New Rule',
            condition: 'When...',
            action: 'Then...',
            type: 'notification',
            isEditing: true,
        };
        setRules([...rules, newRule]);
    };

    const handleDeleteRule = (id: string) => {
        setRules(rules.filter((rule) => rule.id !== id));
    };

    const handleRuleChange = (id: string, field: keyof Rule, value: string) => {
        setRules(rules.map((rule) => (rule.id === id ? {
 ...rule,
[field]: value
} : rule)));
    };

    const handleEditRule = (id: string) => {
        setEditingRule(id);
    };

    const handleSaveRule = (id: string) => {
        setEditingRule(id);
    };

    const handleCancelEdit = (id: string) => {
        setEditingRule(id);
    };

    return (
        <Card className="border shadow-sm h-full w-full">
            <CardHeader className="bg-muted/30 pb-3 pt-4">
                <div className="flex items-center justify-between">
                    <CardTitle>Rules</CardTitle>
                    <div className="flex items-center gap-2">
                        <div className="relative">
                            <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input
                                placeholder="Search rules..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-8 pr-4 w-60"
                            />
                        </div>
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button onClick={handleAddRule} size="sm">
                                        <Plus className="mr-2 h-4 w-4" /> Add Rule
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent>Add a new rule to the table</TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="p-0 h-[calc(100%-5rem)]">
                <div className="h-full overflow-auto">
                    <Table className="w-full">
                        <TableHeader className="sticky top-0 bg-background">
                            <TableRow>
                                <TableHead className="w-[200px]">Rule Name</TableHead>
                                <TableHead className="w-[120px]">Type</TableHead>
                                <TableHead>Condition</TableHead>
                                <TableHead>Action</TableHead>
                                <TableHead className="w-[100px] text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filteredRules.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} className="h-24 text-center text-muted-foreground">
                                        {searchTerm ? 'No rules match your search.' : 'No rules found. Add your first rule.'}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredRules.map((rule) => (
                                    <TableRow key={rule.id} className="group">
                                        <TableCell className="font-medium">
                                            {editingRule === rule.id ? (
                                                <Input
                                                    value={rule.name}
                                                    onChange={(e) => handleRuleChange(rule.id, 'name', e.target.value)}
                                                    placeholder="Rule name"
                                                    className="w-full"
                                                />
                                            ) : (
                                                rule.name
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {editingRule === rule.id ? (
                                                <Select value={rule.type} onValueChange={(value) => handleRuleChange(rule.id, 'type', value)}>
                                                    <SelectTrigger className="w-full">
                                                        <SelectValue placeholder="Select rule type" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {ruleTypes.map((type) => (
                                                            <SelectItem key={type.value} value={type.value}>
                                                                {type.label}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            ) : (
                                                <Badge variant="outline" className={`flex items-center ${getRuleTypeColor(rule.type)}`}>
                                                    {getRuleTypeIcon(rule.type)}
                                                    {rule.type.charAt(0).toUpperCase() + rule.type.slice(1)}
                                                </Badge>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {editingRule === rule.id ? (
                                                <Input
                                                    value={rule.condition}
                                                    onChange={(e) => handleRuleChange(rule.id, 'condition', e.target.value)}
                                                    placeholder="When..."
                                                    className="w-full"
                                                />
                                            ) : (
                                                <div className="bg-muted/30 p-2 rounded-md text-sm">{rule.condition}</div>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {editingRule === rule.id ? (
                                                <Input
                                                    value={rule.action}
                                                    onChange={(e) => handleRuleChange(rule.id, 'action', e.target.value)}
                                                    placeholder="Then..."
                                                    className="w-full"
                                                />
                                            ) : (
                                                <div className="bg-muted/30 p-2 rounded-md text-sm">{rule.action}</div>
                                            )}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            {editingRule === rule.id ? (
                                                <div className="flex justify-end space-x-2">
                                                    <TooltipProvider>
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <Button size="sm" onClick={() => handleSaveRule(rule.id)}>
                                                                    <Save className="h-4 w-4" />
                                                                </Button>
                                                            </TooltipTrigger>
                                                            <TooltipContent>Save changes</TooltipContent>
                                                        </Tooltip>
                                                    </TooltipProvider>
                                                    <TooltipProvider>
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <Button size="sm" variant="outline" onClick={() => handleCancelEdit(rule.id)}>
                                                                    <X className="h-4 w-4" />
                                                                </Button>
                                                            </TooltipTrigger>
                                                            <TooltipContent>Cancel editing</TooltipContent>
                                                        </Tooltip>
                                                    </TooltipProvider>
                                                </div>
                                            ) : (
                                                <div className="flex justify-end space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                    <TooltipProvider>
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <Button variant="ghost" size="icon" onClick={() => handleEditRule(rule.id)}>
                                                                    <Edit className="h-4 w-4" />
                                                                </Button>
                                                            </TooltipTrigger>
                                                            <TooltipContent>Edit rule</TooltipContent>
                                                        </Tooltip>
                                                    </TooltipProvider>
                                                    <TooltipProvider>
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <Button
                                                                    variant="ghost"
                                                                    size="icon"
                                                                    onClick={() => handleDeleteRule(rule.id)}
                                                                    className="text-muted-foreground hover:text-destructive"
                                                                >
                                                                    <Trash className="h-4 w-4" />
                                                                </Button>
                                                            </TooltipTrigger>
                                                            <TooltipContent>Delete rule</TooltipContent>
                                                        </Tooltip>
                                                    </TooltipProvider>
                                                </div>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>
            </CardContent>
        </Card>
    );
}